import { Bericht, GeneriekBericht, GeneriekBerichtOnderwerp } from "@/entities/bericht";
import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { queryClient, useApiClient } from "@/infrastructure/rest";
import { BerichtEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { BerichtDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { BERICHTEN_QUERY_KEY } from "./constants";

function getDossierBerichtenByIdQuery(id: string, apiClient: ReturnType<typeof useApiClient>) {
    return {
        queryKey: [DOSSIERS_QUERY_KEY, id, BERICHTEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<BerichtDto[]>(`/dossiers/${id}/berichten`);
            return data?.map((dto) => Bericht.fromDto(dto)).sort(Bericht.sort) ?? [];
        },
    };
}

export function usePrefetchBerichtenByIdQuery(dossierId: string) {
    const apiClient = useApiClient();
    const query = getDossierBerichtenByIdQuery(dossierId, apiClient);
    return () => queryClient.prefetchQuery({ ...query, staleTime: 60_000 });
}

export function useGetDossierBerichtenById<T = Bericht[]>(
    id: string,
    options?: Omit<UseQueryOptions<Bericht[], Error, T>, "queryFn" | "queryKey">
) {
    const apiClient = useApiClient();
    const query = getDossierBerichtenByIdQuery(id, apiClient);

    return useQuery({
        ...query,
        ...options,
    });
}

export function useGetDossierBerichtenByOnderwerp(dossierId: string, onderwerp: GeneriekBerichtOnderwerp | GeneriekBerichtOnderwerp[]) {
    const onderwerpen = Array.isArray(onderwerp) ? onderwerp : [onderwerp];

    return useGetDossierBerichtenById<GeneriekBericht[]>(dossierId, {
        select: (berichten) => berichten.filter(Bericht.isType("Generiek")).filter((bericht) => onderwerpen.includes(bericht.onderwerp)),
    });
}

export function useRealtimeBerichten() {
    const { handleEvent } = useUpdateDossierPropertyQueriesData("berichten");

    useRealtimeTopic<BerichtEvent>("berichten", {
        onMessage: (message) => {
            const { bericht, dossierId, reason } = message;
            handleEvent(dossierId, bericht.id, reason, Bericht.fromDto(bericht), Bericht.sort);
        },
    });
}

type PostGeneriekBericht = {
    dossierId: string;
    bericht: string;
    onderwerp: GeneriekBerichtOnderwerp;
};

export function usePostGeneriekBericht() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, bericht, onderwerp }: PostGeneriekBericht) => {
            await apiClient.post(`/dossiers/${dossierId}/berichten/generiek`, {
                bericht: bericht,
                onderwerp: onderwerp,
            });
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting bericht:", error);
        },
    });
}

type PostSmsBericht = {
    dossierId: string;
    bericht: string;
    ontvangers: string[];
};

export function usePostSmsBericht() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, bericht, ontvangers }: PostSmsBericht) => {
            await apiClient.post(`/dossiers/${dossierId}/berichten/sms`, {
                bericht: bericht,
                ontvangers: ontvangers,
            });
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting bericht:", error);
        },
    });
}
